<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import Button from 'primevue/button';

interface Props {
  isProgress: boolean;
  isSuccess: boolean;
  isRevert: boolean;
  isAddTour?: boolean;
}
defineProps<Props>();

const emits = defineEmits<{ handleClose: [value: boolean] }>();

const { t } = useI18n({ useScope: 'global' });

const handleClose = () => {
  emits('handleClose', false);
};

const renderTitle = (isRevert: boolean) => {
  if (isRevert) {
    return t('common.labels.revert_in_progress');
  } else {
    return t('common.labels.import_in_progress');
  }
};

const renderMessage = (isSuccess: boolean, isRevert: boolean) => {
  if (isRevert) {
    return isSuccess ? t('common.labels.revert_completed') : t('common.labels.revert_failed');
  } else {
    return isSuccess ? t('common.labels.import_completed') : t('common.labels.import_failed');
  }
};

const renderTourMessage = (isSuccess: boolean) => {
  return isSuccess ? t('management.tour.confirm.tour_added') : t('management.tour.confirm.tour_add_fail');
};
</script>

<template>
  <div :class="['modal-gqt', { 'automated-tour-modal': isAddTour }]">
    <div name="modal-gqt p-5">
      <div v-if="isProgress">
        <div>
          <p>
            <span class="text-xl font-bold title">{{ renderTitle(isRevert) }}</span> <span class="animation"></span><span class="animation"></span><span class="animation"></span><span class="animation"></span>
          </p>
        </div>
      </div>
      <div v-else class="text-center">
        <div>
          <div v-if="isAddTour" class="text-xl font-bold message" :class="!isSuccess ? 'text-primary' : ''">
            <p>{{ renderTourMessage(isSuccess) }}</p>
            <p v-if="!isSuccess">{{ t('management.tour.confirm.tour_add_fail_hint') }}</p>
          </div>
          <p v-else class="text-xl font-bold message">{{ renderMessage(isSuccess, isRevert) }}</p>
        </div>
        <div class="mt-3">
          <Button data-testid="btn_close" :label="$t('common.buttons.close')" class="p-button" :class="!isSuccess ? 'btn_close_modal_fail' : 'btn_close_modal'" @click="handleClose()" />
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.modal-gqt {
  min-width: 25rem;
  min-height: 7rem;
  display: flex;
  align-items: center;
  justify-content: center;
}
.automated-tour-modal {
  max-width: 30rem;
}
.animation {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background-color: $col-bluepurple;
  display: inline-block;
  margin-left: 0.5rem;
  animation: scaling 2.5s ease-in-out infinite;
}

.title {
  color: $col-bluepurple;
}

.message {
  color: $col-greenlaurel;
}
.btn_close_modal {
  background: $col-greenlaurel;
}

.btn_close_modal:enabled:hover {
  background: $col-greenlaurel;
}

.btn_close_modal_fail {
  background: $col-primary;
}

.btn_close_modal_fail:enabled:hover {
  background: $col-primary;
}
@keyframes scaling {
  50% {
    opacity: 0;
    transform: scale(0.7) translateY(3px);
  }
}

.animation:nth-child(1) {
  animation-delay: 0.4s;
}

.animation:nth-child(2) {
  animation-delay: 0.8s;
}

.animation:nth-child(3) {
  animation-delay: 1.2s;
}
.animation:nth-child(4) {
  animation-delay: 1.6s;
}
</style>
